import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { configButtonStylesContained } from '@old/styles/generic-styles';
import ChevronLeftBlue from '@old/assets/svg/core/chevron-left-blue.svg';
import Vector from '@old/assets/svg/Vector.svg';
import { useService } from '@old/hooks/use-service';
import { SilometrixThresholdListService } from '@old/services/domain/device/silometrix-threshold-list';
import { Dispatch, SetStateAction, useState } from 'react';
import { useEffectOnUpdate, useToggle } from '@old/hooks/core';
import { useNavigate } from 'react-router';
import { SilometrixThresholdTable } from './silometrix-threshold-table';
import { updateAlertCycleDataService } from '@old/services/domain/device/silometrix-alert-cycle-edition';
import { useGlobalStore } from '@old/global-store/use-store';
import { SilometrixThresholdEditionService } from '@old/services/domain/device/silometrix-threshold-edition';
import { ToastAction } from '@old/ui/toast/toast-action';
import i18n from '../../../../old/libs/language';
import { SilometrixThresholdDefaultService } from '@old/services/domain/device/silometrix-threshold-default';
import { PATH } from '@old/data/core/paths';

type SilometrixThreshold = Awaited<ReturnType<typeof SilometrixThresholdListService>>['summary']['response'];

type SilometrixMetricsAlerts = SilometrixThreshold['metrics_alerts_configs'];

type SilometrixThresholdDefault = Awaited<
  ReturnType<typeof SilometrixThresholdDefaultService>
>['summary']['response'][0];

export function SilometrixThreshold() {
  const { organizationId } = useGlobalStore();
  const [isUpdatedError, setIsUpdatedError] = useToggle(false);
  const [isUpdatedSuccess, setIsUpdatedSuccess] = useToggle(false);
  const [buttonResetValue, setResetButtonValue] = useToggle(false);
  const [disableSaveButton, setDisableSaveButton] = useToggle(true);
  const [formHasErrors, setFormHasErrors] = useToggle(false);
  const [changeAlertCycle, setChangeAlertCycle] = useToggle(false);

  const {
    summary: silometrixThresholdList,
    isLoading,
    trigger: getSilometrixThresholdList,
  } = useService(SilometrixThresholdListService, {
    fetchOnMount: true,
  });

  const { summary: silometrixThresholdDefault } = useService(SilometrixThresholdDefaultService, {
    fetchOnMount: true,
  });

  // Servicio para actualizar Cantidad de dias para resolucion automatica de alertas de movimiento
  const { trigger: updateAlertCycle } = useService(updateAlertCycleDataService);

  const onSuccessUpdateThresholdData = () => {
    setIsUpdatedError(false);
    setIsUpdatedSuccess(true);
    setDisableSaveButton(true);
    getSilometrixThresholdList();
  };

  const onErrorUpdateThresholdData = () => {
    setIsUpdatedError(true);
    setIsUpdatedSuccess(false);
  };

  // Servicio para actualizar los valores de umbrales de  metricas
  const {
    trigger: updateThreshold, //
  } = useService(SilometrixThresholdEditionService, {
    onSuccess: onSuccessUpdateThresholdData,
    onError: onErrorUpdateThresholdData,
  });

  const SEVERITY_LV = 1;
  const HEG_METRIC = 5;
  const CO2_METRIC = 3;
  const COLOR_ID = 3;

  const originalAlertCycle = silometrixThresholdList?.response.alert_cycle;

  const VALIDATION = {
    ONLY_NUMS: (value: string) => /^[0-9]*$/.test(value),
    FLOAT_NUMS: (value: string) => /^[0-9]*((?<=\d)\.(?=\d))?[0-9]*$/.test(value),
    TYPING_FLOAT_NUMS: (value: string) => /^[0-9]*\.?[0-9]*$/.test(value),
    BETWEEN_0_AND_100: (value: string) => /^(\d{0,2}|\d{0,2}[.]|\d{0,2}[.]\d{1,2}|100)$/.test(value),
  };

  const initialRows = silometrixThresholdList?.response.metrics_alerts_configs
    .filter((rows) => rows.severity === SEVERITY_LV)
    .sort((a, b) => a.species_type_description.localeCompare(b.species_type_description));

  const byHegMetric = (row: SilometrixMetricsAlerts[0]) =>
    row.metric_id === HEG_METRIC && row.severity === SEVERITY_LV;
  const byCo2Metric = (row: SilometrixMetricsAlerts[0]) =>
    row.metric_id === CO2_METRIC && row.severity === SEVERITY_LV;

  const hegDefaultValues = silometrixThresholdDefault?.response.filter(
    (row) => row.metric_id === HEG_METRIC && row.color_id === COLOR_ID
  );
  const co2DefaultValues = silometrixThresholdDefault?.response.filter(
    (row) => row.metric_id === CO2_METRIC && row.color_id === COLOR_ID
  );

  const [alertCycle, setAlertCycle] = useState<number | undefined | string>(undefined);
  const [hegRows, setHegRows] = useState<SilometrixMetricsAlerts>([]);
  const [co2Rows, setCo2Rows] = useState<SilometrixMetricsAlerts>([]);
  const [originalRowsValue, setOriginalRowsValues] = useState<SilometrixThresholdDefault[]>([]);

  useEffectOnUpdate(() => {
    silometrixThresholdDefault && setOriginalRowsValues(silometrixThresholdDefault['response']);
  }, [silometrixThresholdDefault]);

  useEffectOnUpdate(() => {
    const hegList =
      initialRows
        ?.filter(byHegMetric)
        .sort((a, b) => a.species_type_description.localeCompare(b.species_type_description)) ?? [];
    const co2List =
      initialRows
        ?.filter(byCo2Metric)
        .sort((a, b) => a.species_type_description.localeCompare(b.species_type_description)) ?? [];
    setHegRows(hegList);
    setCo2Rows(co2List);
  }, [silometrixThresholdList]);

  const handleHegRowChange: Dispatch<SetStateAction<SilometrixMetricsAlerts>> = (newValue) =>
    setHegRows(newValue);
  const handleCo2RowChange: Dispatch<SetStateAction<SilometrixMetricsAlerts>> = (newValue) =>
    setCo2Rows(newValue);

  const handleAlertCycleChange = (value: string) => {
    if (VALIDATION.ONLY_NUMS(value)) {
      setAlertCycle(value);
      setChangeAlertCycle(true);
    }
    setDisableSaveButton(false);
  };

  const handleHasErrorsChange: Dispatch<SetStateAction<boolean>> = (newValue) => setFormHasErrors(newValue);

  const navigate = useNavigate();

  useEffectOnUpdate(() => {
    setAlertCycle(originalAlertCycle);
  }, [silometrixThresholdList]);

  const handlerReturnOriginalValues = () => {
    setAlertCycle(30);

    const updatedHegRows = hegRows.map((row) => {
      const defaultValue = hegDefaultValues?.find(
        (def) =>
          def.metric_id === row.metric_id && def.silobag_species_type_id === row.silobag_species_type_id
      );
      if (defaultValue) {
        return {
          ...row,
          min_value: defaultValue.min_value,
          max_value: defaultValue.max_value,
        };
      }
      return row;
    });

    const updatedCo2Rows = co2Rows.map((row) => {
      const defaultValue = co2DefaultValues?.find(
        (def) =>
          def.metric_id === row.metric_id && def.silobag_species_type_id === row.silobag_species_type_id
      );
      if (defaultValue) {
        return {
          ...row,
          min_value: defaultValue.min_value,
          max_value: defaultValue.max_value,
        };
      }
      return row;
    });

    setHegRows(updatedHegRows);
    setCo2Rows(updatedCo2Rows);
    setDisableSaveButton(false);
  };

  const areRowsDifferent = (
    originalRows: SilometrixMetricsAlerts,
    currentRows: SilometrixMetricsAlerts
  ): boolean => {
    if (originalRows.length !== currentRows.length) return true;
    return currentRows.some((currentRow, index) => {
      const originalRow = originalRows[index];
      return (
        currentRow.metric_id !== originalRow?.metric_id ||
        currentRow.silobag_species_type_id !== originalRow?.silobag_species_type_id ||
        currentRow.min_value !== originalRow?.min_value ||
        currentRow.max_value !== originalRow?.max_value
      );
    });
  };

  useEffectOnUpdate(() => {
    const hegHasChanges = areRowsDifferent(initialRows?.filter(byHegMetric) ?? [], hegRows);
    const co2HasChanges = areRowsDifferent(initialRows?.filter(byCo2Metric) ?? [], co2Rows);
    (hegHasChanges || co2HasChanges || changeAlertCycle) &&
    !formHasErrors &&
    parseInt(alertCycle as string) > 0
      ? setDisableSaveButton(false)
      : setDisableSaveButton(true);
  }, [hegRows, co2Rows, initialRows]);

  const handlerSaveNewData = () => {
    const movementNotificationBody = { alert_cycle: parseInt(alertCycle as string) };

    updateAlertCycle({ query: organizationId as number, body: movementNotificationBody });

    const originalHegList = initialRows?.filter(byHegMetric) ?? [];
    const originalCo2List = initialRows?.filter(byCo2Metric) ?? [];

    const hegChangedRows = hegRows
      ?.filter((hegRow, index) => {
        const originalRow = originalHegList?.[index];
        return hegRow.min_value !== originalRow?.min_value || hegRow.max_value !== originalRow?.max_value;
      })
      .map((hegRow) => ({
        metric_id: hegRow.metric_id,
        silobag_species_type_id: hegRow.silobag_species_type_id,
        threshold_1: parseFloat(hegRow.min_value as string),
        threshold_2: parseFloat(hegRow.max_value as string),
      }));

    const co2ChangedRows = co2Rows
      .filter((co2Row, index) => {
        const originalRow = originalCo2List?.[index];
        return co2Row.min_value !== originalRow?.min_value || co2Row.max_value !== originalRow?.max_value;
      })
      .map((co2Row) => ({
        metric_id: co2Row.metric_id,
        silobag_species_type_id: co2Row.silobag_species_type_id,
        threshold_1: parseFloat(co2Row.min_value as string),
        threshold_2: parseFloat(co2Row.max_value as string),
      }));

    const allChangedRows = [...hegChangedRows, ...co2ChangedRows];

    updateThreshold({ body: allChangedRows! });
  };

  return (
    <>
      {isLoading ? (
        <Box width={'100vw'} height={'100vh'} display={'flex'} justifyContent={'center'}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {isUpdatedSuccess && (
            <ToastAction severity="success" message={i18n.t('app.silometrix_threshold.threshold_success')} />
          )}
          {isUpdatedError && (
            <ToastAction severity="error" message={i18n.t('app.administrator.error_unexpected')} />
          )}
          <Stack
            direction={{ sx: 'column', md: 'row' }}
            width={'100%'}
            padding={1}
            display={'flex'}
            justifyContent={'start'}
          >
            <Box display={'flex'} justifyContent={'start'}>
              <Button
                startIcon={<img src={ChevronLeftBlue} alt="return" />}
                sx={{ color: '#2fabf0' }}
                onClick={() => navigate(PATH.ADMINISTRATOR)}
              >
                {i18n.t('app.return')}
              </Button>
            </Box>

            <Stack
              direction={'row'}
              width={'100%'}
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Typography variant="h5" fontWeight={500} sx={{ color: '#49454F' }}>
                {i18n.t('app.silometrix_threshold.parameterzation_alerts')}
              </Typography>
              <Button
                variant="contained"
                size="medium"
                disabled={disableSaveButton}
                sx={configButtonStylesContained}
                onClick={handlerSaveNewData}
              >
                {i18n.t('app.save')}
              </Button>
            </Stack>
          </Stack>
          <Box
            sx={{ backgroundColor: '#FFFFFF' }}
            padding={2}
            display={'flex'}
            alignItems={'center'}
            marginRight={{ xs: 1, md: 3 }}
            marginLeft={{ xs: 1, md: 3 }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} width={'100%'} justifyContent={'space-between'}>
              <Box display={'flex'} alignItems={'center'} paddingLeft={1}>
                <Typography marginRight={1} width={{ xs: '80%', md: '40%' }} textAlign={'start'}>
                  {i18n.t('app.silometrix_threshold.count_days_msg')}
                </Typography>
                <TextField
                  sx={{ width: { xs: '15%', md: '10%' }, backgroundColor: '#FFFFFF' }}
                  value={alertCycle}
                  onChange={(e) => handleAlertCycleChange(e.target.value)}
                />
              </Box>
              <Box display={'flex'} justifyContent={{ xs: 'center', md: 'end' }}>
                <Button
                  startIcon={<img src={Vector} alt="vector" />}
                  sx={{ color: '#2fabf0' }}
                  onClick={handlerReturnOriginalValues}
                >
                  {i18n.t('app.silometrix_threshold.reset_original_values')}
                </Button>
              </Box>
            </Stack>
          </Box>
          {originalRowsValue && originalRowsValue?.length > 0 && (
            <SilometrixThresholdTable
              hegDefaultValues={hegDefaultValues}
              co2DefaultValues={co2DefaultValues}
              silometrixData={initialRows!}
              buttonResetValue={buttonResetValue}
              setResetButtonValue={setResetButtonValue}
              setHegRows={handleHegRowChange}
              setCo2Rows={handleCo2RowChange}
              setFormHasErrors={handleHasErrorsChange}
              hegRows={hegRows}
              co2Rows={co2Rows}
              validation={VALIDATION}
            />
          )}
        </Box>
      )}
    </>
  );
}
